import { IFields } from "@tedivo/tedivo-form";
import { INsdImportFields } from "./import-nsd.component";
import LenUnitsEnum from "../../../app/enums/LenUnitsEnum";
import { getTranslation } from "../../../app/i18/i18tn";
import globalUnits from "../../../app/units/globalUnits";
import { z } from "zod";

export function createFormValidator({
  withNameFields = false,
  fileAlreadyLoaded = false,
}: {
  withNameFields?: boolean;
  /**
   * True once the NSD file has been selected and is held by the component.
   *
   * `TedivoForm.setFields()` rebuilds every control, and a file input's value cannot
   * be restored programmatically, so after a rebuild the control is legitimately empty
   * while the File is still available. Without this the form can never be submitted.
   */
  fileAlreadyLoaded?: boolean;
} = {}): z.Schema<INsdImportFields> {
  if (!withNameFields) {
    return z.object({
      fileInput: z.any().refine((f) => fileAlreadyLoaded || f !== undefined, {
        message: "FileIsRequired",
      }),
      imoCode: z
        .string()
        .min(6)
        .max(9)
        .transform((v) => v?.toUpperCase())
        .refine((imoCode) => /^\d{6,8}$/.test(imoCode) || /^[A-Z]{1,2}\d{6,7}$/.test(imoCode)),
    }) as z.Schema<INsdImportFields>;
  }

  return z
    .object({
      fileInput: z.any().refine((f) => fileAlreadyLoaded || f !== undefined, {
        message: "FileIsRequired",
      }),
      imoCode: z
        .string()
        .min(6)
        .max(9)
        .transform((v) => v?.toUpperCase())
        .refine((imoCode) => /^\d{6,8}$/.test(imoCode) || /^[A-Z]{1,2}\d{6,7}$/.test(imoCode)),
      lineOperator: z
        .string()
        .min(1)
        .transform((v) => v?.toUpperCase()),
      shipClass: z
        .string()
        .min(1)
        .transform((v) => v?.toUpperCase()),
      shipName: z
        .string()
        .min(1)
        .transform((v) => v?.toUpperCase()),
      callSign: z.string().optional(),
      yearBuilt: z.number().optional(),
      // Both are optional: the converter falls back to what the .nsd itself
      // carries when they are not sent.
      lpp: z.number().optional(),
      loa: z.number().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.lpp && data.loa && data.lpp >= data.loa) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: getTranslation("errors:formCustom.lppLessThanLoa"),
          path: ["lpp"],
        });

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: getTranslation("errors:formCustom.loadGreaterThanLpp"),
          path: ["loa"],
        });
      }
    });
}

export function createFormFields({
  imoCode,
  fileLabel = "general:openNsd.fileLabel",
  lppFromApi = 0,
  loaFromApi = 0,
  withNameFields = undefined,
}: {
  imoCode: string;
  fileLabel?: string;
  lppFromApi?: number;
  loaFromApi?: number;
  withNameFields?: {
    lineOperator: string;
    shipClass: string;
    shipName: string;
  };
}): IFields<INsdImportFields> {
  // Common
  const fields: IFields<INsdImportFields> = [
    {
      type: "title",
      label: "view:importNsd.file",
      createSection: true,
    },
    {
      name: "fileInput",
      label: fileLabel,
      helpText: "general:openNsd.browseButtonText",
      type: "file",
      accept: ".nsd",
    },
    {
      name: "imoCode",
      label: "view:imoCode",
      type: "textBox",
      autocapitalize: true,
      initialValue: imoCode || "",
      helpText: "view:imoCodeHelp",
    },
  ];

  if (!withNameFields) return fields;

  const lengthUnitsHelpText = getTranslation(
    `enums:LenUnitsEnum.${LenUnitsEnum[globalUnits.lengthUnits.units]}`,
  );

  fields.push(
    {
      type: "title",
      label: "view:importNsd.shipName",
      createSection: true,
    },
    [
      {
        name: "shipName",
        label: "view:shipName",
        type: "textBox",
        autocapitalize: true,
        initialValue: withNameFields.shipName || "",
      },
      {
        name: "lineOperator",
        label: "view:lineOperator",
        type: "textBox",
        autocapitalize: true,
        initialValue: withNameFields.lineOperator || "",
      },
      {
        name: "shipClass",
        label: "view:shipClass",
        type: "textBox",
        autocapitalize: true,
        initialValue: withNameFields.shipClass || "",
      },
    ],
    [
      {
        name: "callSign",
        label: "view:callSign",
        type: "textBox",
        autocapitalize: true,
      },
      {
        name: "yearBuilt",
        label: "view:yearBuilt",
        type: "number",
      },
    ],
    {
      type: "title",
      label: "view:importNsd.dimensions",
      createSection: true,
    },
    {
      name: "lpp",
      label: "view:lppHelpText",
      type: "numberWithUnits",
      converter: globalUnits.lengthUnits,
      initialValue: lppFromApi,
      helpText: lengthUnitsHelpText,
    },
    {
      name: "loa",
      label: "view:loa",
      type: "numberWithUnits",
      converter: globalUnits.lengthUnits,
      initialValue: loaFromApi,
      helpText: lengthUnitsHelpText,
    },
  );

  return fields;
}
